/**
 * Display helpers for the couple-facing day-of timeline and task widgets
 * (/ai-timeline, /timeline, Home). Pure functions, no React — tested in
 * client/src/lib/__tests__/timelineDisplay.test.ts.
 *
 * Portal audit 2026-09-24: couples were shown the venue's internal run sheet
 * (generator start, AC setup, role slugs like valet_team, staff names, vendor
 * instructions, "Family Instructions: None"), events out of time order, and
 * point-in-time events as "5:30 PM - 5:30 PM, 0 mins". These helpers produce the
 * couple view; staff views keep the raw data.
 */

import { toLocalDateString, formatDateDisplay } from "@/lib/dateUtils";
import { resolveWeddingPackage, type PackageRowLike, type WeddingPackageRef } from "@shared/weddingPackage";

/** Roles that get the client portal (mirrors CLIENT_PORTAL_ROLES in App.tsx). */
export const COUPLE_ROLES = ["client", "collaborator", "planner"];

export function isCoupleRole(role: string | null | undefined): boolean {
  return !!role && COUPLE_ROLES.includes(role);
}

/** Escape text before it goes into HTML written to a print window. */
export function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** "18:30" / "18:30:00" → minutes after midnight, or null. */
export function parseHHMM(time: string | null | undefined): number | null {
  if (!time) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(time.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** Minutes after midnight → "5:30 PM". */
export function formatClock(minutes: number): string {
  const m = ((minutes % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${mm} ${ampm}`;
}

/** "18:30" → "6:30 PM"; unparseable input is returned as-is ("TBD" when empty). */
export function formatTimeText(time: string | null | undefined): string {
  const mins = parseHHMM(time);
  if (mins === null) return time || "TBD";
  return formatClock(mins);
}

export interface TimelineEventLike {
  id?: number;
  eventTitle?: string | null;
  eventDescription?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  duration?: number | null;
  category?: string | null;
  familyInstructions?: string | null;
  sortOrder?: number | null;
}

/** True for moments with no length (duration 0, or start == end, or no end). */
export function isPointInTime(e: TimelineEventLike): boolean {
  if (e.duration === 0) return true;
  const s = parseHHMM(e.startTime);
  const en = parseHHMM(e.endTime);
  if (s === null || en === null) return !e.duration;
  return s === en;
}

/** "5:30 PM" for moments, "6:30 PM – 7:00 PM" for spans. */
export function formatEventTime(e: TimelineEventLike): string {
  if (isPointInTime(e)) return formatTimeText(e.startTime);
  return `${formatTimeText(e.startTime)} – ${formatTimeText(e.endTime)}`;
}

/** 90 → "1 hr 30 min", 45 → "45 min", 0/null → "". */
export function formatDuration(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h} hr ${m} min`;
  if (h) return `${h} hr`;
  return `${m} min`;
}

/**
 * Sort by start time. Events before 4 AM on a timeline that starts at or after noon are
 * treated as after midnight (a reception ending 00:30 goes last, not first). Ties and
 * unparseable times keep the server's sortOrder.
 */
export function sortEventsByTime<T extends TimelineEventLike>(events: T[], timelineStart?: string | null): T[] {
  const anchor = parseHHMM(timelineStart ?? null);
  const eveningTimeline = anchor === null ? true : anchor >= 12 * 60;
  const key = (e: T) => {
    const s = parseHHMM(e.startTime);
    if (s === null) return Number.MAX_SAFE_INTEGER;
    return eveningTimeline && s < 4 * 60 ? s + 1440 : s;
  };
  return events
    .map((e, i) => ({ e, i }))
    .sort((a, b) => {
      const d = key(a.e) - key(b.e);
      if (d !== 0) return d;
      const so = (a.e.sortOrder ?? a.i) - (b.e.sortOrder ?? b.i);
      return so !== 0 ? so : a.i - b.i;
    })
    .map((x) => x.e);
}

/** "valet_team" → "Valet team", "pre-event" → "Pre event". */
export function humanizeSlug(value: string | null | undefined): string {
  if (!value) return "";
  const s = value.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Text worth showing: null for empty, "None", "N/A", "-" and similar. */
export function meaningfulText(value: string | null | undefined): string | null {
  if (!value) return null;
  const t = value.trim();
  if (!t || /^(none|n\/?a|nil|null|-+|no|not applicable)\.?$/i.test(t)) return null;
  return t;
}

const CATEGORY_LABELS: Record<string, string> = {
  "pre-event": "Before guests arrive",
  preparation: "Getting ready",
  ceremony: "Ceremony",
  reception: "Reception",
  photography: "Photos",
  catering: "Food",
  decoration: "Decor",
  entertainment: "Entertainment",
  "post-event": "After the event",
};

export function categoryLabel(category: string | null | undefined): string {
  if (!category) return "Other";
  const key = category.trim().toLowerCase();
  return CATEGORY_LABELS[key] || humanizeSlug(category);
}

/** Venue-operations categories couples don't need (unless the event has family instructions). */
const OPERATIONAL_CATEGORIES = [
  "pre-event",
  "setup",
  "logistics",
  "operations",
  "technical",
  "facility",
  "maintenance",
  "teardown",
  "cleanup",
  "post-event",
  "staff",
];

/**
 * The couple's view of the schedule: venue-operations steps (generator start, AC
 * setup...) are left out unless they carry real instructions for the family
 * (e.g. "Valet parking opens — direct guests to valet"). Sorted by time.
 */
export function coupleVisibleEvents<T extends TimelineEventLike>(events: T[], timelineStart?: string | null): T[] {
  const visible = events.filter((e) => {
    const cat = (e.category || "").trim().toLowerCase();
    if (!OPERATIONAL_CATEGORIES.includes(cat)) return true;
    return meaningfulText(e.familyInstructions) !== null;
  });
  return sortEventsByTime(visible, timelineStart);
}

/** "11:00 AM to 3:00 PM" / "6 PM - 10 PM" → minutes; null when not a time range. */
export function parseSlot(text: string | null | undefined): { start: number; end: number } | null {
  if (!text) return null;
  const re = /(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/gi;
  const found: number[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) && found.length < 2) {
    let h = Number(m[1]) % 12;
    if (m[3].toUpperCase() === "PM") h += 12;
    found.push(h * 60 + Number(m[2] || 0));
  }
  if (found.length < 2) return null;
  let [start, end] = found;
  if (end <= start) end += 1440;
  return { start, end };
}

export interface WeddingLike {
  weddingDate?: string | Date | null;
  timeSlot?: string | null;
  weddingTime?: string | null;
  notes?: string | null;
  venue?: string | null;
  brideName?: string | null;
  groomName?: string | null;
}

/**
 * Weddings that get a day-of timeline generator: everything except cancelled bookings
 * (2026-09-25 review gap G11 — the server also refuses them with 409).
 */
export function timelineEligibleWeddings<T extends { status?: string | null }>(weddings: T[] | null | undefined): T[] {
  if (!Array.isArray(weddings)) return [];
  return weddings.filter((w) => String(w?.status || "").toLowerCase() !== "cancelled");
}

/** The wedding's slot text ("11:00 AM to 3:00 PM"), ignoring contract-id junk. */
export function weddingSlotText(w: WeddingLike | null | undefined): string | null {
  if (!w) return null;
  for (const v of [w.timeSlot, w.weddingTime]) {
    if (v && !v.includes("CONTRACT_ID") && v.trim()) return v.trim();
  }
  const m = w.notes?.match(/Time Slot:\s*([^,\[\n]+)/);
  return m ? m[1].trim() : null;
}

/** Package name from notes ("Package: Coral"), or null — never a made-up default.
 *  Legacy: timeline screens use weddingPackageName (package_id first) instead. */
export function packageNameFromNotes(notes: string | null | undefined): string | null {
  if (!notes || !notes.includes("Package:")) return null;
  const name = notes.split("Package:")[1].split(/[,\n\[\]]/)[0].trim();
  return name || null;
}

/**
 * The wedding's package for display, resolved exactly as the server's timeline generator
 * does (shared/weddingPackage.ts): weddings.package_id -> wedding_packages, then an explicit
 * "Package:" label naming a real package, else null. Review gap G7 (2026-09-25).
 */
export function weddingPackageName(
  wedding: WeddingPackageRef | null | undefined,
  packages: PackageRowLike[] | null | undefined,
): string | null {
  return resolveWeddingPackage(wedding, packages)?.name ?? null;
}

/**
 * Does the saved timeline still match the booking? Detects a timeline generated for an
 * older date, or whose guest-facing events fall outside the booked time slot.
 */
export function timelineMismatch(
  wedding: WeddingLike | null | undefined,
  timeline: { weddingDate?: string | Date | null; startTime?: string | null } | null | undefined,
  events: TimelineEventLike[],
): { date: boolean; time: boolean } {
  const result = { date: false, time: false };
  if (!wedding || !timeline) return result;
  const wd = toLocalDateString(wedding.weddingDate as any);
  const td = toLocalDateString(timeline.weddingDate as any);
  if (wd && td && wd !== td) result.date = true;

  const slot = parseSlot(weddingSlotText(wedding));
  if (slot) {
    const starts = coupleVisibleEvents(events, timeline.startTime)
      .map((e) => parseHHMM(e.startTime))
      .filter((n): n is number => n !== null);
    if (starts.length) {
      const first = starts[0];
      // Allow 3 hours before the slot (getting ready, photos) but not past its end.
      if (first < slot.start - 180 || first > slot.end) result.time = true;
    }
  }
  return result;
}

/** Human date for the couple in IST, e.g. "14 Oct 2028"; "" when unknown. */
export function weddingDateText(value: string | Date | null | undefined): string {
  return formatDateDisplay(value as any);
}

// ---------------------------------------------------------------------------
// Calendar (.ics)
// ---------------------------------------------------------------------------

function icsEscape(v: string): string {
  return v.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** IST wall-clock (YYYY-MM-DD + minutes) → UTC ICS stamp "YYYYMMDDTHHMMSSZ". */
function istToUtcStamp(dateYmd: string, minutes: number): string {
  const [y, mo, d] = dateYmd.split("-").map(Number);
  const utcMs = Date.UTC(y, mo - 1, d, 0, 0) + (minutes - 330) * 60_000;
  const u = new Date(utcMs);
  return `${u.getUTCFullYear()}${pad(u.getUTCMonth() + 1)}${pad(u.getUTCDate())}T${pad(u.getUTCHours())}${pad(u.getUTCMinutes())}00Z`;
}

/**
 * A single calendar event for the wedding day. Uses the booked slot when it can be
 * parsed, otherwise an all-day event. Returns null when the date is unknown.
 */
export function buildWeddingIcs(
  wedding: WeddingLike & { id?: number },
  now: Date = new Date(),
): string | null {
  const ymd = toLocalDateString(wedding.weddingDate as any);
  if (!ymd) return null;
  const names = [wedding.brideName, wedding.groomName].filter(Boolean).join(" & ");
  const title = names ? `${names} — Wedding` : "Wedding";
  const slot = parseSlot(weddingSlotText(wedding));
  const stamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//LA Mirage//Wedding Timeline//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:wedding-${wedding.id ?? ymd}@lamirage.co`,
    `DTSTAMP:${stamp}`,
  ];
  if (slot) {
    lines.push(`DTSTART:${istToUtcStamp(ymd, slot.start)}`, `DTEND:${istToUtcStamp(ymd, slot.end)}`);
  } else {
    const [y, mo, d] = ymd.split("-").map(Number);
    const next = new Date(Date.UTC(y, mo - 1, d + 1));
    lines.push(
      `DTSTART;VALUE=DATE:${ymd.replace(/-/g, "")}`,
      `DTEND;VALUE=DATE:${next.getUTCFullYear()}${pad(next.getUTCMonth() + 1)}${pad(next.getUTCDate())}`,
    );
  }
  lines.push(`SUMMARY:${icsEscape(title)}`);
  if (wedding.venue) lines.push(`LOCATION:${icsEscape(wedding.venue)}`);
  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\r\n") + "\r\n";
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export interface TaskLike {
  id: number;
  title?: string | null;
  category?: string | null;
  isCompleted?: boolean | null;
  dueDate?: string | Date | null;
}

/** Staff live-chat follow-ups are not the couple's tasks (also filtered server-side). */
export function isCoupleTask(t: TaskLike): boolean {
  return t.category !== "live_chat";
}

export function taskProgress(tasks: TaskLike[] | null | undefined): { done: number; total: number; percent: number } {
  const list = Array.isArray(tasks) ? tasks.filter(isCoupleTask) : [];
  const done = list.filter((t) => t.isCompleted).length;
  const total = list.length;
  return { done, total, percent: total ? Math.round((done / total) * 100) : 0 };
}

/** Open tasks first by due date (no date last), then completed ones. */
export function orderTasksForDisplay<T extends TaskLike>(tasks: T[]): T[] {
  const due = (t: T) => {
    const d = t.dueDate ? new Date(t.dueDate).getTime() : NaN;
    return isNaN(d) ? Number.MAX_SAFE_INTEGER : d;
  };
  return tasks
    .filter(isCoupleTask)
    .map((t, i) => ({ t, i }))
    .sort((a, b) => {
      const c = Number(!!a.t.isCompleted) - Number(!!b.t.isCompleted);
      if (c !== 0) return c;
      const d = due(a.t) - due(b.t);
      return d !== 0 ? d : a.i - b.i;
    })
    .map((x) => x.t);
}
